import { Context } from '../..';
import { getSubdomain } from '../../../library/helpers/get-subdomain';
import { RatingScale } from '../../../models/rating-scale';
import * as base from '../base';
import { CompanySearchResponse } from '../../../models/view/company-search-response';
import { Company } from '../../../models/view/company';
import { Entity } from '../../../models/view/entity';
import { BaseSearchRequest } from '../../../models/overmind/search-request';
import { CompanyVariables, defaultCompanyVariables } from '../../../models/view/company-variables';
import { NotificationPriority, NotificationType } from '../../../notifications/notifications.models';
import { Country } from '../../../pages/onboarding/classic/classic.onboarding.layout.models';
import { RegistrationType } from '../../../enums/registration-type';

const CONTROLLER = `company`;

// TODO: apply error handling
export async function setSubDomain(): Promise<void> {
  const init = new base.DefaultAuthenticatedParameters({ method: 'GET' });
  try {
    const domainParts = window.location.host.split('.');
    if (domainParts.length >= 3) {
      const data = await fetch(base.apiURL(CONTROLLER, `get-subdomain`), init.toObject());
      const response = await data.text();

      let subDomainSplit = '-sg-';
      if (domainParts[0].indexOf('-qa-') !== -1) {
        subDomainSplit = '-qa-';
      }
      if (domainParts[0].indexOf('-dev-') !== -1) {
        subDomainSplit = '-dev-';
      }

      const subdomain = domainParts[0].split(subDomainSplit)[0];
      if (subdomain !== response) {
        window.location.assign(window.location.toString().replace(subdomain, response));
      }
    }
  } catch (ex) {
    console.log(ex);
  }
}

// CMS Endpoints
export async function searchCompanies(context: Context, SearchRequest: BaseSearchRequest): Promise<CompanySearchResponse | undefined> {
  const queryParams = new URLSearchParams(SearchRequest as any).toString();

  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `get-companies?${queryParams}`), authenticated: true };
  const response: base.IResponse<CompanySearchResponse> = await base.request(request);

  return response.data;
}

export async function getCountries(context: Context, payload?: string): Promise<Country[] | undefined> {
  const url = base.apiURL(CONTROLLER, `get-countries`);
  if (!!payload) url.concat(`?countryName=${payload}`);

  const request: base.IRequest = { url, authenticated: true };
  const response: base.IResponse<Country[]> = await base.request(request);

  return response.data;
}

export async function getIndustries(context: Context, industryName?: string): Promise<Entity[] | undefined> {
  const url = base.apiURL(CONTROLLER, `get-industries`);
  if (!!industryName) url.concat(`?industryName=${industryName}`);

  const request: base.IRequest = { url, authenticated: true };
  const response: base.IResponse<Entity[]> = await base.request(request);

  return response.data;
}

export async function getCompany(context: Context, companySlug: string): Promise<Company | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `get-company`, companySlug), authenticated: true };
  const response: base.IResponse<Company> = await base.request(request);
  return response.data;
}

export async function saveCompany(context: Context, company: Company): Promise<Company | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `save-company`), authenticated: true, method: 'POST', body: JSON.stringify(company) };
  const response: base.IResponse<Company> = await base.request(request);

  if (response.error) {
    context.actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Error,
      priority: NotificationPriority.Toast,
      title: response.error.detail,
    });
  }

  return response.data;
}

export async function getCompanyRatingScales(): Promise<RatingScale | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `get-company-rating-scale`), authenticated: true };
  const response: base.IResponse<RatingScale> = await base.request(request);
  return response.data;
}

export async function getUserCompanyVariables(context: Context): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `variables`), authenticated: true, method: 'GET' };
  const response: base.IResponse<CompanyVariables> = await base.request(request);
  if (!!response.data) {
    context.state.companyVariables = {
      ...response.data,
      slug: context.state.companyVariables.slug,
    };
  } else {
    context.state.companyVariables = {
      ...defaultCompanyVariables,
      slug: context.state.companyVariables.slug,
    };
  }
}

export async function getCompanyVariables(context: Context, slug: string): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `variables`, slug), authenticated: true, method: 'GET' };
  const response: base.IResponse<CompanyVariables> = await base.request(request);
  context.state.companyVariables = !!response.data
    ? {
      ...response.data,
      slug,
    }
    : {
      ...defaultCompanyVariables,
      slug,
    };
}

// refactored
export async function setRegistrationType(context: Context): Promise<void> {
  const subdomain = getSubdomain();
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `registration-type`, subdomain) };
  const response: base.IResponse<RegistrationType> = await base.request(request);
  context.state.registrationType = !!response.data ? response.data : RegistrationType.NotAllowed;
}

export async function setSamlUrl(context: Context): Promise<void> {
  const subdomain = getSubdomain();
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `get-saml-url`, subdomain) };
  const response: base.IResponse<string> = await base.request(request);
  context.state.samlUrl = response.data;
}